import { defineMessages } from '@formatjs/intl';
import { ApiWorkOrderState } from '@allbin/mobilix-api-client';
import { DateTime } from 'luxon';
import { IntlShape } from 'react-intl';
import { MapMarker } from '../components/Map';
import { MapData, MapDataUpdate, MarkerData } from '../store/map';
import { getFilterMarkerIconUrl } from './colors';

const messages = defineMessages({
  stop: {
    id: 'stop',
    defaultMessage: 'Stop',
  },
  station: {
    id: 'station',
    defaultMessage: 'Station',
  },
  work_order: {
    id: 'work_order',
    defaultMessage: 'Work order',
  },
  inactive: {
    id: 'inactive',
    defaultMessage: 'Inactive',
  },
  position: {
    id: 'map.stop_info.position',
    defaultMessage: 'Position',
  },
  stopClass: {
    id: 'map.stop_info.class',
    defaultMessage: 'Class',
  },
  passingRoutes: {
    id: 'map.stop_info.passing_routes',
    defaultMessage: 'Passing routes',
  },
  inUse: {
    id: 'map.stop_info.in_use',
    defaultMessage: 'In use',
  },
  notInUse: {
    id: 'map.stop_info.not_in_use',
    defaultMessage: 'Not in use',
  },
  openWorkOrders: {
    id: 'map.stop_info.open_work_orders',
    defaultMessage:
      '{count, plural, one {# ongoing work order} other {# ongoing work orders}}',
  },
});

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// States a work order cannot leave again. Everything else counts as ongoing,
// including `completed` (awaiting approval) and `rejected` (sent back for more
// work), since both can still move back into an active state.
const finishedWorkOrderStates = new Set<ApiWorkOrderState>([
  'approved',
  'cancelled',
]);

export const countOngoingWorkOrdersByEntity = (
  workOrders: WorkOrder[],
): Record<string, number> =>
  workOrders.reduce<Record<string, number>>((counts, wo) => {
    if (finishedWorkOrderStates.has(wo.state)) {
      return counts;
    }
    for (const entityId of wo.entities) {
      counts[entityId] = (counts[entityId] || 0) + 1;
    }
    return counts;
  }, {});

/**
 * Builds the markup for the info card shown when hovering a stop marker,
 * styled after a Google Maps place card: a white rounded card with the stop
 * name as the title, the position ("läge") and class as a gray subtitle, the
 * passing routes as yellow pill badges and an "in use" status indicator.
 * It is rendered inside a Google Maps `InfoWindow`, so this returns a raw HTML
 * string (with the default InfoWindow chrome stripped) rather than React nodes.
 *
 * `routeNameLookup` (see `useRouteNameLookup`) maps a passing route's short
 * name to its route name; when a match is found it is shown in parentheses next
 * to the route, e.g. "461 (Centrumlinjen)".
 *
 * `ongoingWorkOrderCount` (see `countOngoingWorkOrdersByEntity`) is the number
 * of unfinished work orders including this stop; the row is left out when there
 * are none.
 */
export const buildStopInfoTooltipHtml = (
  intl: IntlShape,
  entity: Entity,
  routeNameLookup: Record<string, string> = {},
  ongoingWorkOrderCount = 0,
): string => {
  const name =
    (entity.properties['meta.name'] as string) || entity.full_name || '';
  const stopLetter = entity.stop_letter || '';
  const passingRoutes = Array.isArray(entity.properties['meta.passing_routes'])
    ? (entity.properties['meta.passing_routes'] as string[])
    : [];
  const isActive = entity.properties['meta.active'] !== false;

  // "Läge" and the stop class form the gray subtitle, e.g. "Läge A · Klass 2".
  const positionText = stopLetter
    ? `${intl.formatMessage(messages.position)} ${stopLetter}`
    : '';
  const rawClass = entity.properties['meta.class'];
  const stopClass =
    typeof rawClass === 'string' || typeof rawClass === 'number'
      ? `${rawClass}`.trim()
      : '';
  const classText = stopClass
    ? `${intl.formatMessage(messages.stopClass)} ${stopClass}`
    : '';
  const subtitle = [positionText, classText]
    .filter((part) => part.length > 0)
    .map((part) => escapeHtml(part))
    .join(' · ');

  // Passing routes are shown as yellow (primary) pill badges under a heading.
  // When the GTFS lookup resolves a full route name it is appended in lighter
  // parentheses; pills may wrap so long names stay within the card.
  const routePills = passingRoutes
    .map((route) => {
      const routeName = routeNameLookup[route];
      const label = routeName
        ? `${escapeHtml(
            route,
          )} <span style="font-weight:400;margin-left:5px;">(${escapeHtml(
            routeName,
          )})</span>`
        : escapeHtml(route);
      return (
        `<span style="display:inline-flex;align-items:center;` +
        `background:rgb(var(--primary-500, 234 179 8));` +
        `color:rgb(var(--contrast-primary, 18 22 38));` +
        `border-radius:9999px;padding:2px 9px;font-size:12px;font-weight:600;` +
        `line-height:1.5;">${label}</span>`
      );
    })
    .join('');
  const routesSection = passingRoutes.length
    ? `<div style="margin-top:8px;">` +
      `<div style="font-size:11px;color:#5f6368;font-weight:600;margin-bottom:4px;">` +
      `${escapeHtml(intl.formatMessage(messages.passingRoutes))}</div>` +
      `<div style="display:flex;flex-wrap:wrap;gap:4px;">${routePills}</div>` +
      `</div>`
    : '';

  // Ongoing work orders, shown as a blue dot with a count, matching the layout
  // of the status row below.
  const workOrderRow =
    ongoingWorkOrderCount > 0
      ? `<div style="display:flex;align-items:center;gap:6px;margin-top:8px;` +
        `font-size:12px;color:#5f6368;">` +
        `<span style="width:8px;height:8px;border-radius:50%;background:#2e86de;` +
        `display:inline-block;flex-shrink:0;"></span>` +
        `<span>${escapeHtml(
          intl.formatMessage(messages.openWorkOrders, {
            count: ongoingWorkOrderCount,
          }),
        )}</span>` +
        `</div>`
      : '';

  // "In use" status, shown as a colored dot with a label (green/red).
  const statusColor = isActive ? '#1e8e3e' : '#d93025';
  const statusText = isActive
    ? intl.formatMessage(messages.inUse)
    : intl.formatMessage(messages.notInUse);
  const statusRow =
    `<div style="display:flex;align-items:center;gap:6px;margin-top:8px;` +
    `font-size:12px;color:#5f6368;">` +
    `<span style="width:8px;height:8px;border-radius:50%;background:${statusColor};` +
    `display:inline-block;flex-shrink:0;"></span>` +
    `<span>${escapeHtml(statusText)}</span>` +
    `</div>`;

  return (
    `<style>` +
    `.gm-style-iw-chr { display: none !important; }` +
    `.gm-style-iw-c { background: transparent !important; box-shadow: none !important; padding: 0 !important; border-radius: 12px !important; }` +
    `.gm-style-iw-d { overflow: visible !important; }` +
    `.gm-style-iw-tc { display: none !important; }` +
    `</style>` +
    `<div style="` +
    `background:#fff;` +
    `border-radius:12px;` +
    `box-shadow:0 2px 10px rgba(0,0,0,0.25);` +
    `color:#202124;` +
    `font-family:'Open Sans', sans-serif;` +
    `padding:12px 14px;` +
    `min-width:150px;` +
    `max-width:280px;">` +
    `<div style="font-weight:700;font-size:15px;line-height:1.3;">` +
    `${escapeHtml(name)}</div>` +
    (subtitle
      ? `<div style="font-size:12.5px;color:#5f6368;margin-top:2px;">${subtitle}</div>`
      : '') +
    routesSection +
    workOrderRow +
    statusRow +
    `</div>`
  );
};

export const locationPropertyByTenant = (tenant: string): string => {
  switch (tenant) {
    case 'region-vastmanland':
      return 'meta.location';
    default:
      return 'inventory.location';
  }
};

export type MarkerEventCB = (
  eventType: MarkerEventType,
  markerData: MarkerData,
  mousePos?:
    | {
        x: number;
        y: number;
      }
    | undefined,
) => void;
export type MarkerEventType = 'click' | 'enter' | 'leave';

const createEventCB = (
  type: MarkerEventType,
  cb: MarkerEventCB,
  markerData: MarkerData,
): ((
  markerInstance: google.maps.Marker,
  mouseEvent: google.maps.MapMouseEvent,
) => void) => {
  //TODO: Use MouseEvent to get latlng and convert to screenX/Y.
  // return (markerInstance, mouseEvent) => cb(type, markerData, { x: 0, y: 0 });
  return () => cb(type, markerData, { x: 0, y: 0 });
};

export const getCoordinatesFromEntities = (
  tenant: string,
  entities: Entity[],
): Record<string, [number, number]> => {
  return entities.reduce<Record<string, [number, number]>>(
    (coordByEntityID, e) => {
      const location =
        (e.properties[locationPropertyByTenant(tenant)] as [number, number]) ??
        (e.properties['meta.location'] as [number, number]);
      coordByEntityID[e.id] = location;
      return coordByEntityID;
    },
    {},
  );
};

export const getCoordinatesFromWorkorderChangesets = (
  workorder?: WorkOrder,
): Record<string, [number, number]> => {
  return !workorder
    ? {}
    : Object.values(workorder.entity_changesets)
        .filter((cs) => {
          return (
            !!cs.properties['meta.location'] ||
            !!cs.properties['inventory.location']
          );
        })
        .reduce<Record<string, [number, number]>>((coordByEntityID, cs) => {
          const location =
            (cs.properties['inventory.location'] as [number, number]) ??
            (cs.properties['meta.location'] as [number, number]);
          coordByEntityID[cs.entity_id] = location;
          return coordByEntityID;
        }, {});
};

export const updateMarkers = (
  map: google.maps.Map,
  newData: MapData['markers'],
  staticMarkerRefs: Record<string, MapMarker>,
  markerEventCB: MarkerEventCB,
): void => {
  //Loop all markers in store and update markers if necessary.
  Object.values(newData).forEach((newMarkerData) => {
    const existing = staticMarkerRefs[newMarkerData.id];
    if (!existing) {
      //Marker is new, never been added to map.
      const style = {
        ...newMarkerData.style,
        position: newMarkerData.position,
        title: newMarkerData.title,
      };
      const newMarker = new google.maps.Marker(style);
      newMarker.set('id', newMarkerData.id);
      newMarker.setMap(map);
      //No, it's not really deprecated?
      newMarker.addListener(
        'click',
        createEventCB('click', markerEventCB, newMarkerData),
      );
      newMarker.addListener(
        'mouseover',
        createEventCB('enter', markerEventCB, newMarkerData),
      );
      newMarker.addListener(
        'mouseout',
        createEventCB('leave', markerEventCB, newMarkerData),
      );
      staticMarkerRefs[newMarkerData.id] = {
        markerData: newMarkerData,
        gmapsMarker: newMarker,
      };
      return;
    }
    if (existing.markerData !== newMarkerData) {
      //Marker has changed.
      const style = {
        ...newMarkerData.style,
        position: newMarkerData.position,
      };
      existing.gmapsMarker.setOptions(style);
      staticMarkerRefs[newMarkerData.id] = {
        ...existing,
        markerData: newMarkerData,
      };
      return;
    }
    //Unchanged
  });

  //Loop all existing markers and remove any that are no longer in store.
  Object.entries(staticMarkerRefs).forEach(([markerId, mapMarker]) => {
    if (!newData[markerId]) {
      //This marker no longer exist in store. Remove it.
      mapMarker.gmapsMarker.unbindAll();
      mapMarker.gmapsMarker.setMap(null);
      delete staticMarkerRefs[markerId];
    }
  });

  return;
};

const workOrderTimeIcons = {
  inactive: `/map_icons/inactive.svg`,
  good: `/map_icons/wo_ok.png`,
  warning: `/map_icons/wo_warning.png`,
  final: `/map_icons/wo_final.png`,
  bad: `/map_icons/wo_expired.png`,
};

export const mapMarkersFromWorkOrders = (
  workOrders: WorkOrder[],
  visibleWorkOrderIds: string[],
  entityLookup: Record<string, Entity>,
  intl: IntlShape,
  existingMarkers: Record<string, MarkerData> = {},
  deleteUnusedExisting = true,
): Record<string, MarkerData> => {
  const markers: Record<string, MarkerData> = {};

  for (const wo of workOrders) {
    const daysRemaining =
      wo.due_at && Math.floor(wo.due_at.diffNow('days').days);
    let iconUrl = workOrderTimeIcons.good;
    if (wo.state === 'completed') {
      iconUrl = workOrderTimeIcons.good;
    } else if (wo.state === 'cancelled') {
      iconUrl = workOrderTimeIcons.bad;
    } else if (wo.state === 'approved') {
      iconUrl = workOrderTimeIcons.final;
    } else if (
      typeof daysRemaining === 'number' &&
      daysRemaining <= 3 &&
      daysRemaining >= 0
    ) {
      iconUrl = workOrderTimeIcons.warning;
    } else if (typeof daysRemaining === 'number' && daysRemaining < 0) {
      iconUrl = workOrderTimeIcons.bad;
    }
    for (const entityId of wo.entities) {
      const entity = entityLookup[entityId];
      if (!entity) {
        continue;
      }
      const id = wo.id + ':' + entityId;
      const existing = existingMarkers[id];
      const [lng, lat] =
        (entity.properties['inventory.location'] as [number, number]) ??
        (entity.properties['meta.location'] as [number, number]);
      if (
        !existing ||
        existing.style.icon !== iconUrl ||
        existing.position.lat !== lat ||
        existing.position.lng !== lng ||
        existing.style.visible !== visibleWorkOrderIds.includes(wo.id)
      ) {
        //No existing marker or the wo has changed after the
        //marker was created, update marker data.
        markers[id] = {
          style: {
            icon: iconUrl,
            draggable: false,
            clickable: true,
            visible: visibleWorkOrderIds.includes(wo.id),
          },
          id: id,
          type: 'workorder',
          updated_at: DateTime.now(),
          title: `${intl.formatMessage(messages.stop)}: ${
            entity.full_name
          }\n${intl.formatMessage(messages.work_order)}: ${wo.title}#${
            wo.title_suffix
          }`,
          position: {
            lat,
            lng,
          },
        };
        continue;
      } else if (existing) {
        //The existing marker data is up to date, pass it along untouched.
        markers[id] = existing;
        continue;
      }
    }
  }

  if (!deleteUnusedExisting) {
    Object.values(existingMarkers).forEach((existing) => {
      if (!markers[existing.id]) {
        markers[existing.id] =
          existing.style.visible === true
            ? {
                ...existing,
                style: {
                  ...existing.style,
                  visible: false,
                },
              }
            : existing;
      }
    });
  }

  return markers;
};

export const mapMarkersFromEntities = (
  intl: IntlShape,
  tenant: string,
  entities: Entity[],
  entityCoords: Record<string, [number, number]>,
  workorderCoords: Record<string, [number, number]>,
  existingMarkers: Record<string, MarkerData>,
  showStopLetter = true,
  deleteUnusedExisting = false,
): Record<string, MarkerData> => {
  const markers: Record<string, MarkerData> = {};

  for (const entity of entities) {
    const isInactive = entity.properties['meta.active'] === false;
    const entityMarkerID = entity.id;
    const workOrderMarkerID = entity.id + ':workorder';
    const existingEntityMarker = existingMarkers[entityMarkerID];
    const existingWorkOrderMarker = existingMarkers[workOrderMarkerID];
    const [lng, lat] = entityCoords[entityMarkerID]
      ? [entityCoords[entityMarkerID][0], entityCoords[entityMarkerID][1]]
      : (entity.properties[locationPropertyByTenant(tenant)] as [
          number,
          number,
        ]) ?? (entity.properties['meta.location'] as [number, number]);
    if (!workorderCoords[entity.id]) {
      if (
        !existingEntityMarker ||
        existingEntityMarker.position.lat !== lat ||
        existingEntityMarker.position.lng !== lng
      ) {
        //No existing marker or the wo has changed after the
        //marker was created, update marker data.
        markers[entityMarkerID] = {
          style: {
            icon: {
              url: isInactive
                ? workOrderTimeIcons.inactive
                : workOrderTimeIcons.warning,
            },
            draggable: false,
            clickable: true,
            visible: true,
            label: showStopLetter ? entity.stop_letter : undefined,
          },
          id: entityMarkerID,
          type: entity.entity_type_id === 'station' ? 'entityGroup' : 'entity',
          updated_at: DateTime.now(),
          title: isInactive
            ? `${entity.full_name} [${intl.formatMessage(messages.inactive)}]`
            : entity.full_name,
          position: {
            lat,
            lng,
          },
        };
      } else if (existingEntityMarker) {
        //The existing marker data is up to date, pass it along untouched.
        markers[entityMarkerID] = existingEntityMarker;
      }
    } else {
      const [wlng, wlat] = [
        workorderCoords[entityMarkerID][0],
        workorderCoords[entityMarkerID][1],
      ];
      if (
        !existingWorkOrderMarker ||
        existingWorkOrderMarker.position.lat !== wlat ||
        existingWorkOrderMarker.position.lng !== wlng
      ) {
        //No existing marker or the wo has changed after the
        //marker was created, update marker data.
        markers[workOrderMarkerID] = {
          style: {
            icon: {
              url: isInactive
                ? workOrderTimeIcons.inactive
                : workOrderTimeIcons.warning,
            },
            draggable: false,
            clickable: true,
            visible: true,
            label: showStopLetter ? entity.stop_letter : undefined,
          },
          id: workOrderMarkerID,
          type: entity.entity_type_id === 'station' ? 'entityGroup' : 'entity',
          updated_at: DateTime.now(),
          title: entity.full_name,
          position: {
            lat: wlat,
            lng: wlng,
          },
        };
      } else if (existingWorkOrderMarker) {
        //The existing marker data is up to date, pass it along untouched.
        markers[workOrderMarkerID] = existingWorkOrderMarker;
      }
    }
  }

  if (!deleteUnusedExisting) {
    Object.values(existingMarkers).forEach((existing) => {
      if (!markers[existing.id]) {
        markers[existing.id] =
          existing.style.visible === true
            ? {
                ...existing,
                style: {
                  ...existing.style,
                  visible: false,
                },
              }
            : existing;
      }
    });
  }

  return markers;
};

// Marker z-index layering. Stops that belong to a selected bus route are drawn
// above regular stops so they stay visible (on top) within dense clusters. The
// highlight ring sits between the two: above regular stops, but behind its own
// on-route stop pin.
export const STOP_Z_INDEX = 10000;
export const STOP_ON_SELECTED_ROUTE_HIGHLIGHT_Z_INDEX = 15000;
export const STOP_ON_SELECTED_ROUTE_Z_INDEX = 20000;

export const mapDataFromFilterResults = (
  tenant: string,
  results: ExtendedFilterQueryResults,
  mapId: string,
  existingMarkers: Record<string, MarkerData> = {},
  stopsOnSelectedRoutes: Set<string> = new Set(),
  deleteUnusedExisting = false,
): MapDataUpdate => {
  const markers: Record<string, MarkerData> = {};

  const lastActiveFilterIndex = results.steps.findLastIndex(
    (s) => s.filter.active,
  );
  const firstActiveFilterIndex = results.steps.findIndex(
    (s) => s.filter.active,
  );

  const lastActiveColor =
    lastActiveFilterIndex >= 0
      ? results.steps[lastActiveFilterIndex].filter.color
      : undefined;

  const resultsToShow: {
    entities: Entity[];
    filterIndex: number;
    color?: string | null;
  }[] = [
    {
      entities: results.final,
      filterIndex: lastActiveFilterIndex,
      color: lastActiveColor,
    },
  ];
  if (results.showUnmatched && results.steps.length) {
    results.steps.forEach((step, i) => {
      if (!step.filter.active) {
        return;
      }
      if (i === firstActiveFilterIndex) {
        resultsToShow.push({ filterIndex: -1, entities: step.unmatched });
      }
      resultsToShow.push({
        filterIndex: i,
        color: step.filter.color,
        entities: step.matched,
      });
    });
  }

  resultsToShow.forEach(({ entities, filterIndex, color }) => {
    for (const e of entities) {
      const existing = existingMarkers[e.id];
      const isInactive = e.properties['meta.active'] === false;
      const zIndex =
        (stopsOnSelectedRoutes.has(e.id)
          ? STOP_ON_SELECTED_ROUTE_Z_INDEX
          : STOP_Z_INDEX) + filterIndex;
      const iconUrl = getFilterMarkerIconUrl(filterIndex, color, isInactive);
      if (
        !existing ||
        existing.style.icon !== iconUrl ||
        existing.style.visible === false
      ) {
        const loc =
          (e.properties[locationPropertyByTenant(tenant)] as [
            number,
            number,
          ]) ?? (e.properties['meta.location'] as [number, number]);
        const lng = loc[0];
        const lat = loc[1];
        markers[e.id] = {
          id: e.id,
          type: 'entity',
          // The hover tooltip (see buildStopInfoTooltipHtml) replaces the
          // native marker title, so no `title` is set here to avoid showing a
          // duplicate browser tooltip on top of the tooltip.
          position: {
            lng,
            lat,
          },
          style: {
            opacity: 1,
            icon: iconUrl,
            draggable: false,
            clickable: true,
            visible: true,
            zIndex,
          },
          updated_at: DateTime.now(),
        };
      } else if (existing) {
        // Icon/visibility are unchanged. Only rebuild the marker data (a new
        // reference, so updateMarkers actually applies it) when the z-index
        // changes, e.g. when the stop joins or leaves a selected route.
        markers[e.id] =
          existing.style.zIndex === zIndex
            ? existing
            : { ...existing, style: { ...existing.style, zIndex } };
      }
    }
  });
  if (!deleteUnusedExisting) {
    Object.values(existingMarkers).forEach((existing) => {
      if (!markers[existing.id]) {
        markers[existing.id] = {
          ...existing,
          style: {
            ...existing.style,
            visible: false,
          },
        };
      }
    });
  }

  return {
    id: mapId,
    markers,
  };
};
